import { useState } from "react";
import { ArrowUpRight, CalendarCheck, Linkedin, Mail, MapPin, Send } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionTag } from "@/components/SectionTag";
import { Breadcrumb } from "@/components/Breadcrumb";
import { SITE } from "@/constants/site";
import { useLang } from "@/i18n/LangContext";

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "", botcheck: false });
  const [status, setStatus] = useState(null);
  const { t } = useLang();
  const c = t.contactPage;

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject: `${c.subjectPrefix} — ${form.name}`,
          from_name: c.fromName,
          name: form.name,
          company: form.company,
          email: form.email,
          message: form.message,
          botcheck: form.botcheck,
        }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error();
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <Seo
        title={c.seo.title}
        description={c.seo.description}
        path="/contact"
        schemas={[
          breadcrumbSchema([
            { name: t.common.home, path: "/" },
            { name: c.breadcrumb, path: "/contact" },
          ]),
        ]}
      />

      <section className="px-5 md:px-10 pt-36 md:pt-44 pb-24 md:pb-32" data-testid="contact">
        <div className="mx-auto max-w-7xl">
          <Breadcrumb items={[{ label: t.common.home, to: "/" }, { label: c.breadcrumb }]} />
          <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
            <div>
              <Reveal>
                <img
                  src="/assets/logo-consultante-strategie-de-marque-pme.png"
                  alt={t.logoAlt}
                  className="mb-8 h-20 w-20 rounded-2xl object-cover shadow-[0_16px_40px_-12px_rgba(17,14,13,0.2)]"
                />
              </Reveal>
              <SectionTag>{c.tag}</SectionTag>
              <Reveal delay={0.1}>
                <h1 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter text-balance">
                  {c.h1}
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-7 text-base md:text-lg leading-relaxed text-plum/85">
                  {c.intro}
                </p>
              </Reveal>

              <div className="mt-12 space-y-4">
                <Reveal delay={0.25}>
                  <a
                    href={SITE.calendly}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-calendly-card"
                    className="group flex items-center gap-5 rounded-3xl bg-ink p-6 text-cream transition-colors duration-300 hover:bg-plum"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-terracotta/20">
                      <CalendarCheck className="w-6 h-6 text-terracotta" />
                    </span>
                    <div className="flex-1">
                      <p className="font-display font-bold tracking-tight">{c.calendlyTitle}</p>
                      <p className="mt-0.5 text-sm text-cream/60">{c.calendlySub}</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-terracotta transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </a>
                </Reveal>
                <Reveal delay={0.3}>
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="contact-email-card"
                    className="group flex items-center gap-5 rounded-3xl border border-ink/5 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-15px_rgba(17,14,13,0.12)]"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-terracotta/15">
                      <Mail className="w-6 h-6 text-terracotta-deep" />
                    </span>
                    <div>
                      <p className="font-display font-bold tracking-tight">{c.emailTitle}</p>
                      <p className="mt-0.5 text-sm text-plum/60">{SITE.email}</p>
                    </div>
                  </a>
                </Reveal>
                <Reveal delay={0.35}>
                  <a
                    href={SITE.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin-card"
                    className="group flex items-center gap-5 rounded-3xl border border-ink/5 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-15px_rgba(17,14,13,0.12)]"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-terracotta/15">
                      <Linkedin className="w-6 h-6 text-terracotta-deep" />
                    </span>
                    <div>
                      <p className="font-display font-bold tracking-tight">{c.linkedinTitle}</p>
                      <p className="mt-0.5 text-sm text-plum/60">{c.linkedinSub}</p>
                    </div>
                  </a>
                </Reveal>
                <Reveal delay={0.4}>
                  <div className="flex items-center gap-5 rounded-3xl border border-ink/5 bg-white p-6">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-terracotta/15">
                      <MapPin className="w-6 h-6 text-terracotta-deep" />
                    </span>
                    <div>
                      <p className="font-display font-bold tracking-tight">{c.zoneTitle}</p>
                      <p className="mt-0.5 text-sm text-plum/60">{t.zones}</p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>

            <Reveal delay={0.2}>
              <div className="rounded-[2rem] border border-ink/5 bg-white p-8 md:p-10 shadow-[0_20px_40px_-10px_rgba(17,14,13,0.05)]">
                <h2 className="font-display text-2xl font-bold tracking-tight">{c.formTitle}</h2>
                <p className="mt-2 text-sm text-plum/70">
                  {c.formSubtitle}
                </p>
                {status === "sent" ? (
                  <div className="mt-8 rounded-3xl bg-terracotta-light/40 p-8 text-center" data-testid="contact-success">
                    <p className="font-display text-xl font-bold tracking-tight">{c.successTitle}</p>
                    <p className="mt-3 text-sm leading-relaxed text-plum/80">
                      {c.successTextA} {form.name}{c.successTextB}
                    </p>
                  </div>
                ) : (
                <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="contact-name" className="text-sm font-semibold">
                      {c.labelName}
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      data-testid="contact-name-input"
                      placeholder={c.placeholderName}
                      className="mt-2 w-full rounded-2xl border border-ink/10 bg-cream/50 px-5 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-terracotta placeholder:text-plum/40"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="text-sm font-semibold">
                      {c.labelCompany}
                    </label>
                    <input
                      id="contact-company"
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      data-testid="contact-company-input"
                      placeholder={c.placeholderCompany}
                      className="mt-2 w-full rounded-2xl border border-ink/10 bg-cream/50 px-5 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-terracotta placeholder:text-plum/40"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-sm font-semibold">
                      {c.labelEmail}
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      data-testid="contact-email-input"
                      placeholder={c.placeholderEmail}
                      className="mt-2 w-full rounded-2xl border border-ink/10 bg-cream/50 px-5 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-terracotta placeholder:text-plum/40"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="text-sm font-semibold">
                      {c.labelMessage}
                    </label>
                    <textarea
                      id="contact-message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      data-testid="contact-message-input"
                      placeholder={c.placeholderMessage}
                      className="mt-2 w-full resize-none rounded-2xl border border-ink/10 bg-cream/50 px-5 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-terracotta placeholder:text-plum/40"
                    />
                  </div>
                  <input
                    type="checkbox"
                    name="botcheck"
                    tabIndex={-1}
                    aria-hidden="true"
                    checked={form.botcheck}
                    onChange={(e) => setForm((f) => ({ ...f, botcheck: e.target.checked }))}
                    style={{ display: "none" }}
                  />
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-submit-button"
                    className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-terracotta px-7 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-terracotta-dark disabled:opacity-60"
                  >
                    <Send className="w-4 h-4" />
                    {status === "sending" ? c.sending : c.submit}
                  </button>
                  {status === "error" && (
                    <p className="text-center text-sm font-medium text-red-700" data-testid="contact-error">
                      {c.errorA} {SITE.email}.
                    </p>
                  )}
                  <p className="text-center text-xs text-plum/50">
                    {c.privacyNote}
                  </p>
                </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
